import { useState, useEffect, useRef, useMemo } from 'react';
import jwt from 'jsonwebtoken';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, CircularProgress } from '@mui/material';
import aspireLogo from '../../../../assets/img/aspire-logo.png';
import ShowMessage from 'components/ShowMessage/ShowMessage';
import FeedbackLoading from '../feedback-loading/FeedbackLoading';
import { feedbackService } from 'modules/Feedback/services/feedback';
import './FeedbackAuthentication.scss';

const CODE_LENGTH = 5;

const FeedbackAuthentication = () => {
  const history = useHistory();
  const location = useLocation();
  const feedbackToken = useMemo(
    () => new URLSearchParams(location.search).get('token'),
    [location.search]
  );
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const [error, setError] = useState('');
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [invalidToken, setInvalidToken] = useState(!feedbackToken);
  const [checkingStatus, setCheckingStatus] = useState(Boolean(feedbackToken));
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);

  const code = digits.join('');
  const responsePath = feedbackToken
    ? `/feedback/response?token=${encodeURIComponent(feedbackToken)}`
    : '/feedback/response';

  useEffect(() => {
    if (!feedbackToken || invalidToken) return;

    const idToken = localStorage.getItem('idToken');
    if (!idToken || idToken === 'undefined') return;
    const decodeToken = jwt.decode(idToken);
    if (String(decodeToken?.feedbackToken) === String(feedbackToken)) {
      history.replace(responsePath);
      return;
    }
    localStorage.removeItem('idToken');
  }, [history, feedbackToken, responsePath, invalidToken]);

  useEffect(() => {
    let cancelled = false;

    if (!feedbackToken) {
      setInvalidToken(true);
      setCheckingStatus(false);
      return undefined;
    }

    setInvalidToken(false);
    setCheckingStatus(true);

    const checkFeedbackStatus = async () => {
      try {
        const feedback = await feedbackService.getFeedbackStatus(feedbackToken);
        if (cancelled) return;
        if (feedback?.data?.status === 'completed') {
          localStorage.removeItem('idToken');
          setAlreadySubmitted(true);
        }
      } catch (err) {
        if (!cancelled && err?.response?.status === 404) {
          localStorage.removeItem('idToken');
          setInvalidToken(true);
        }
      } finally {
        if (!cancelled) setCheckingStatus(false);
      }
    };

    checkFeedbackStatus();
    return () => {
      cancelled = true;
    };
  }, [feedbackToken]);

  useEffect(() => {
    if (!checkingStatus && !alreadySubmitted && !invalidToken && feedbackToken) {
      inputRefs.current[0]?.focus();
    }
  }, [checkingStatus, alreadySubmitted, invalidToken, feedbackToken]);

  const focusInput = index => {
    const next = Math.max(0, Math.min(index, CODE_LENGTH - 1));
    inputRefs.current[next]?.focus();
    inputRefs.current[next]?.select?.();
  };

  const updateDigits = nextDigits => {
    setDigits(nextDigits);
    if (error) setError('');
  };

  const handleDigitChange = (index, value) => {
    const cleaned = value.replace(/\D/g, '');
    if (!cleaned) {
      const next = [...digits];
      next[index] = '';
      updateDigits(next);
      return;
    }

    // Support paste / multi-char into one box
    if (cleaned.length > 1) {
      const next = [...digits];
      cleaned
        .slice(0, CODE_LENGTH - index)
        .split('')
        .forEach((char, offset) => {
          next[index + offset] = char;
        });
      updateDigits(next);
      focusInput(Math.min(index + cleaned.length, CODE_LENGTH - 1));
      return;
    }

    const next = [...digits];
    next[index] = cleaned;
    updateDigits(next);
    if (index < CODE_LENGTH - 1) focusInput(index + 1);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        updateDigits(next);
      } else if (index > 0) {
        focusInput(index - 1);
        const next = [...digits];
        next[index - 1] = '';
        updateDigits(next);
      }
      e.preventDefault();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const next = Array(CODE_LENGTH)
      .fill('')
      .map((_, i) => pasted[i] || '');
    updateDigits(next);
    focusInput(Math.min(pasted.length, CODE_LENGTH - 1));
  };

  const validateCode = () => {
    if (code.length === 0) {
      setError('Code is required.');
      return false;
    }
    if (code.length < CODE_LENGTH) {
      setError('Code must be 5 digits.');
      return false;
    }
    return true;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateCode() || loading || !feedbackToken) return;

    try {
      setLoading(true);
      setError('');
      const response = await feedbackService.feedbackAuthenticate({ code, feedbackToken });
      localStorage.setItem('idToken', response?.data);
      history.push(responsePath);
    } catch (err) {
      if (err?.response?.status === 404) {
        localStorage.removeItem('idToken');
        setInvalidToken(true);
      } else if (err?.response?.status === 403) {
        localStorage.removeItem('idToken');
        setAlreadySubmitted(true);
      } else if (err?.response?.status === 429) {
        setError('Too many attempts. Please try again later.');
      } else {
        setError('Invalid code. Please try again.');
        setDigits(Array(CODE_LENGTH).fill(''));
        focusInput(0);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return <FeedbackLoading message="Checking your access…" />;
  }

  if (invalidToken) {
    return (
      <ShowMessage>
        <h1>Invalid feedback link</h1>
        <p>
          This feedback link is invalid or no longer available. Please use the link from your
          invitation email.
        </p>
      </ShowMessage>
    );
  }

  if (alreadySubmitted) {
    return (
      <ShowMessage>
        <h1>Feedback already submitted</h1>
        <p>Thanks — this feedback was already received. No further action is needed.</p>
      </ShowMessage>
    );
  }

  return (
    <div className="feedbackLogin">
      <div className="feedbackLoginBackdrop" aria-hidden="true" />
      <div className="feedbackLoginCard">
        <img src={aspireLogo} alt="Aspire" className="feedbackLoginBrand" />
        <h1 className="feedbackLoginTitle">Feedback access</h1>
        <p className="feedbackLoginSubtitle">
          Enter the 5-digit code from your invitation to continue.
        </p>
        <form onSubmit={handleSubmit} className="feedbackLoginForm" noValidate>
          <div
            className={`feedbackCodeInputs${error ? ' hasError' : ''}`}
            role="group"
            aria-label="Access code"
          >
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={el => {
                  inputRefs.current[index] = el;
                }}
                className="feedbackCodeBox"
                type="text"
                inputMode="numeric"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                maxLength={1}
                value={digit}
                disabled={loading}
                aria-label={`Digit ${index + 1}`}
                onChange={e => handleDigitChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onFocus={e => e.target.select()}
              />
            ))}
          </div>
          <p className={`feedbackCodeHint${error ? ' isError' : ''}`} aria-live="polite">
            {error || ' '}
          </p>
          <Button
            variant="contained"
            className="feedbackLoginSubmit"
            type="submit"
            disabled={loading || code.length < CODE_LENGTH}
            fullWidth
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default FeedbackAuthentication;
